import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spinner, Alert } from 'react-bootstrap';
import { api } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '–';

const CandidateModal = ({ show, onHide, candidate }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { canView } = useAuth();

  const fetchCandidateDetails = useCallback(async () => {
    if (!candidate || !canView()) return;
    setLoading(true);
    setError('');
    const fallback = () => ({
      success: true,
      candidate_info: {
        name: candidate.name, nationality: candidate.nationality, email: candidate.email,
        phone: candidate.phone, date_of_birth: candidate.date_of_birth, graduation_year: candidate.graduation_year,
      },
      work_experiences: candidate.work_experience,
      headers: ['No', 'Organization', 'Position', 'Employment Period', 'FTE', 'Employment Duration', 'Valid Employment Duration', 'Qualified', 'Overlap'],
      position_analysis: {},
    });
    try {
      const response = await api.getCandidateDetails(candidate.id);
      if (response.data.success) setDetails(response.data);
      else if (candidate.work_experience?.length > 0) setDetails(fallback());
      else setError(response.data.error || 'Failed to load candidate details');
    } catch (err) {
      console.error('Error fetching candidate details:', err);
      if (candidate.work_experience?.length > 0) setDetails(fallback());
      else if (err.response?.status === 401) setError('Authentication required - please sign in');
      else if (err.response?.status === 403) setError('You do not have permission to view candidate details');
      else setError('Failed to load candidate details');
    } finally {
      setLoading(false);
    }
  }, [candidate, canView]);

  useEffect(() => {
    if (show && candidate) fetchCandidateDetails();
  }, [show, candidate, fetchCandidateDetails]);

  const handleDownload = async () => {
    if (!candidate) return;
    try {
      const response = await api.downloadFile(candidate.excel_file);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', candidate.excel_name || 'step_determination.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 401) alert('Authentication required - please sign in');
      else if (error.response?.status === 403) alert('You do not have permission to download files');
      else alert('Error downloading file');
    }
  };

  if (!candidate) return null;

  const sd = candidate.step_details || {};
  const finalStep = candidate.step_determination || 'N/A';
  const isDet = finalStep && finalStep !== 'N/A' && finalStep !== 'Error';
  const pa = details?.position_analysis || {};
  const gapText = pa.experience_gap;
  const noGap = !gapText || ['0', '0.0', '0 years'].includes(gapText) || (typeof gapText === 'string' && gapText.includes('0'));
  // The model's relevance score behind each Yes/No (null when not assessed).
  const threshold = typeof candidate.relevance_threshold === 'number' ? candidate.relevance_threshold : 0.5;
  const hasScore = (exp) => typeof exp.relevance_score === 'number';

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="modal-dialog-fullscreen" scrollable>
      <Modal.Header closeButton className="sheet-head">
        <div className="sh-id">
          <span className="sh-av">{initials(candidate.name)}</span>
          <div>
            <h3>{candidate.name}</h3>
            <div className="sh-sub">Step determination analysis</div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">Loading candidate details…</p>
          </div>
        )}

        {error && <Alert variant="danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</Alert>}

        {details && (
          <>
            {/* Candidate information */}
            <div className="det-card">
              <h4><i className="fas fa-id-card"></i> Candidate information</h4>
              <div className="dc-body">
                <div className="info-grid">
                  <div className="field"><span className="f-k">Name</span><span className="f-v">{details.candidate_info.name || candidate.name}</span></div>
                  <div className="field"><span className="f-k">Nationality</span><span className="f-v">{details.candidate_info.nationality || candidate.nationality}</span></div>
                  <div className="field"><span className="f-k">Email</span><span className="f-v">{details.candidate_info.email || candidate.email}</span></div>
                  <div className="field"><span className="f-k">Phone</span><span className="f-v">{details.candidate_info.phone || candidate.phone}</span></div>
                  <div className="field"><span className="f-k">Date of birth</span><span className="f-v">{details.candidate_info.date_of_birth || candidate.date_of_birth}</span></div>
                  <div className="field"><span className="f-k">Graduation year</span><span className="f-v">{details.candidate_info.graduation_year || candidate.graduation_year}</span></div>
                  <div className="field"><span className="f-k">Highest education</span><span className="f-v">{candidate.highest_education}</span></div>
                  <div className="field"><span className="f-k">Comments</span><span className="f-v">
                    {candidate.relevance_method === 'not_assessed'
                      ? <span className="chip warn">RELEVANCE NOT ASSESSED</span>
                      : candidate.is_qualified
                        ? <span className="chip ok"><i className="fas fa-check"></i> QUALIFIED</span>
                        : <span className="chip no"><i className="fas fa-xmark"></i> NEEDS MORE EXPERIENCE</span>}
                  </span></div>
                </div>
              </div>
            </div>

            {/* WHO step determination */}
            {candidate.step_details && (
              <div className="det-card">
                <h4><i className="fas fa-stairs"></i> WHO step determination</h4>
                <div className="dc-body">
                  <div className="step-hero">
                    <div className="big-step">
                      <b>{isDet ? String(finalStep).replace(/[^0-9]/g, '') || finalStep : '—'}</b>
                      <span>{isDet ? 'Final step' : 'Not determined'}</span>
                    </div>
                    <div className="breakdown">
                      <div className="bd"><b>Step {sd.base_step ?? 1}</b><span>Base step</span></div>
                      <div className="bd"><b>+{sd.education_steps ?? 0}</b><span>Education bonus</span></div>
                      <div className="bd"><b>+{sd.experience_steps ?? 0}</b><span>Experience bonus</span></div>
                      <div className="bd">
                        <b>{sd.policy_compliant
                          ? <span className="chip ok">Yes</span>
                          : <span className="chip warn">Review required</span>}</b>
                        <span>Policy compliant</span>
                      </div>
                    </div>
                  </div>
                  {sd.education_reasoning && <div className="reasoning"><span className="rl">Education reasoning</span>{sd.education_reasoning}</div>}
                  {sd.experience_reasoning && <div className="reasoning"><span className="rl">Experience reasoning</span>{sd.experience_reasoning}</div>}
                  {sd.cap_reasoning && <div className="reasoning"><span className="rl">Step cap applied</span>{sd.cap_reasoning}</div>}
                </div>
              </div>
            )}

            {/* Position match analysis */}
            {details.position_analysis && Object.keys(details.position_analysis).length > 0 && (
              <div className="det-card">
                <h4><i className="fas fa-chart-line"></i> Position match analysis</h4>
                <div className="dc-body">
                  <div className="analysis-grid">
                    <div className="an"><div className="an-k">Target position</div><div className="an-v" style={{ fontSize: '.82rem' }}>{pa.target_position}</div></div>
                    <div className="an"><div className="an-k">Grade</div><div className="an-v">{pa.grade}</div></div>
                    <div className="an"><div className="an-k">Required experience</div><div className="an-v">{pa.required_experience}</div></div>
                    <div className="an"><div className="an-k">Candidate total experience</div><div className="an-v info">{pa.candidate_total_experience}</div></div>
                    <div className="an"><div className="an-k">Total relevant experience</div><div className="an-v warn">{pa.total_relevant_experience}</div></div>
                    <div className="an"><div className="an-k">Experience gap</div><div className={`an-v ${noGap ? 'ok' : ''}`}>{noGap ? 'No gap' : gapText}</div></div>
                    <div className="an"><div className="an-k">Match score</div><div className="an-v ok">{pa.match_score}</div></div>
                    <div className="an"><div className="an-k">Education match</div><div className="an-v">{pa.education_match}</div></div>
                    <div className="an"><div className="an-k">Recommendation</div><div className="an-v" style={{ fontSize: '.82rem' }}>{pa.recommendation}</div></div>
                  </div>
                </div>
              </div>
            )}

            {/* Work experience analysis */}
            {details.work_experiences && details.work_experiences.length > 0 && (
              <div className="det-card">
                <h4><i className="fas fa-briefcase"></i> Work experience analysis ({details.work_experiences.length} positions)</h4>
                <div className="dc-body">
                  <div className="legend">
                    <span><b>FTE</b> · F = Full-time (100%), P = Part-time</span>
                    <span><b>Qualified</b> · Yes = the model scored the post {threshold.toFixed(2)} or above for relevance to the target role; its score and reasoning are shown for HR to confirm</span>
                    <span><b>Valid duration</b> · adjusted for overlapping periods</span>
                  </div>
                  <div className="tablewrap" style={{ boxShadow: 'none' }}>
                    <div className="table-scroll">
                      <table className="who-table">
                        <thead>
                          <tr>{details.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {details.work_experiences.map((exp, index) => (
                            <React.Fragment key={index}>
                              <tr className={hasScore(exp) ? 'has-ai' : undefined} style={{ cursor: 'default' }}>
                                <td>{exp.no}</td>
                                <td>{exp.organization}</td>
                                <td>{exp.position}</td>
                                <td>{exp.employment_period}</td>
                                <td>{exp.fte === 'F' ? <span className="chip ok">Full-time</span> : <span className="chip warn">Part-time</span>}</td>
                                <td>{exp.employment_duration}</td>
                                <td>{exp.valid_employment_duration || exp.employment_duration}</td>
                                <td>{exp.qualified === 'Yes'
                                  ? <span className="chip ok">Yes</span>
                                  : exp.qualified === 'No'
                                    ? <span className="chip neutral">No</span>
                                    : <span className="chip warn">{exp.qualified}</span>}
                                  {hasScore(exp) && <div className="ai-score" title="The model's relevance score, for HR to confirm">Model score {exp.relevance_score.toFixed(2)}</div>}</td>
                                <td>{exp.overlap === 'Yes' ? <span className="chip warn">Yes</span> : <span className="chip ok">No</span>}</td>
                              </tr>
                              {hasScore(exp) && (
                                <tr className="ai-row" style={{ cursor: 'default' }}>
                                  <td></td>
                                  <td colSpan={details.headers.length - 1}>
                                    <div className="ai-note">
                                      <span className="ai-label"><i className="fas fa-robot"></i> Model's assessment, for HR to confirm</span>
                                      Score {exp.relevance_score.toFixed(2)}: {exp.relevance_score >= threshold ? 'relevant' : 'not relevant'} (a post counts as relevant at {threshold.toFixed(2)} or above). {exp.relevance_reasoning}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="sheet-foot">
        <div className="ff"><i className="fas fa-file-excel" style={{ color: 'var(--success)' }}></i> Excel file: {candidate.excel_name}</div>
        <div className="fbtns">
          <button className="btn btn-success btn-sm" onClick={handleDownload}><i className="fas fa-download"></i> Download Excel</button>
          <button className="btn btn-ghost btn-sm" onClick={onHide}><i className="fas fa-xmark"></i> Close</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CandidateModal;
